import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment'
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { LevelAsset } from './assets-data.service';
import { OrganisationDataModelNode } from './organisation-data.service';
import { DataPoint } from './datapoints-data.service';

/**
 * IHistoricalReport
 */
export interface IHistoricalReport {
	
	/** GeneratedReportID */
	GeneratedReportID: number;

	/** OrganisationReportID */
	OrganisationReportID: number;

	/** OrganisationUID */
	OrganisationUID: string;

	/** GeneratedAt */
	GeneratedAt: number;

	/** Base64 */
	Base64: string;

	/** FileType */
	FileType: string;

	/** Name */
	Name: string;

	/** Description */
	Description: string;

	/** FileName */
	FileName: string;
}

/**
 * HistoricalReport
 */
export class HistoricalReport implements IHistoricalReport {

	/**
	 * Default Constructor
	 * @param GeneratedReportID GeneratedReportID 
	 * @param OrganisationReportID OrganisationReportID 
	 * @param OrganisationUID OrganisationUID 
	 * @param GeneratedAt GeneratedAt 
	 * @param Base64 Base64 
	 * @param FileType FileType 
	 * @param Name Name 
	 * @param Description Description
	 * @param FileName FileName  
	 */
	constructor(
		public GeneratedReportID: number,
		public OrganisationReportID: number,
		public OrganisationUID: string,
		public GeneratedAt: number,
		public Base64: string,
		public FileType: string,
		public Name: string,
		public Description: string,
		public FileName: string
	) { }
}

/**
 * ISummaryReport
 */
export interface ISummaryReport {

	/**
	 * name
	 */
	Name: string;

	/**
	 * interval
	 */
	Interval: number;

	/**
	 * fileType
	 */
	FileType: number;

	/**
	 * description
	 */
	Description?: string;

	/**
	 * folderNodes
	 */
	FolderNodes?: any;

	/**
	 * sensorNodes
	 */
	SensorNodes?: any;

	/**
	 * datapointNodes
	 */
	DatapointNodes?: any;

	/**
	 * OrganisationReportID
	 */
	OrganisationReportID?: number;

	/**
	 * OrganisationID
	 */
	OrganisationID?: number;

	/**
	 * FileTypeName
	 */
	FileTypeName?: string;

	/**
	 * IntervalName
	 */
	IntervalName?: string;
}

/**
 * SummaryReport
 */
export class SummaryReport implements ISummaryReport {

	/**
	 * SummaryReport
	 * @param name name 
	 * @param interval interval 
	 * @param fileType fileType 
	 * @param description description 
	 * @param folderNodes folderNodes 
	 * @param sensorNodes sensorNodes 
	 * @param datapointNodes datapointNodes 
	 * @param OrganisationReportID OrganisationReportID 
	 * @param OrganisationID OrganisationID 
	 * @param FileTypeName FileTypeName 
	 * @param IntervalName IntervalName 
	 */
	constructor(
		public Name: string,
		public Interval: number,
		public FileType: number,
		public Description?: string,
		public FolderNodes?: any,
		public SensorNodes?: any,
		public DatapointNodes?: any,
		public OrganisationReportID?: number,
		public OrganisationID?: number,
		public FileTypeName?: string,
		public IntervalName?: string
	) { }
}

/**
 * IDatapointReportItem
 */
export interface IDatapointReportItem {

	/** DatapointEnergyMetric */
	DatapointEnergyMetric: DatapointEnergyMetric;

	/** AssetName */
	AssetName: string;

	/** DPTitle */
	DPTitle: string;

	/** ParentSensorID */
	ParentSensorID?: number;
}

/**
 * DatapointReportItem
 */
export class DatapointReportItem implements IDatapointReportItem {

	/**
	 * Default Constructor
	 * @param DatapointEnergyMetric DatapointEnergyMetric  
	 * @param AssetName AssetName 
	 * @param DPTitle DPTitle 
	 * @param ParentSensorID ParentSensorID 
	 */
	constructor(
		public DatapointEnergyMetric: DatapointEnergyMetric,
		public AssetName: string,
		public DPTitle: string,
		public ParentSensorID?: number
	) { }
}

/**
 * ITotalisedReportItem
 */
export interface ITotalisedReportItem {

	/** TotalisedEnergyMetric */
	TotalisedEnergyMetric: TotalisedEnergyMetric;

	/** NodeName */
	NodeName: string;

	/** NodeUID */
	NodeUID: string;

	/** ParentUID */
	ParentUID?: string;

	/** Units */
	Units?: string;
}

/**
 * TotalisedReportItem
 */
export class TotalisedReportItem implements ITotalisedReportItem {

	/**
	 * Default Constructor
	 * @param TotalisedEnergyMetric TotalisedEnergyMetric 
	 * @param NodeName NodeName 
	 * @param NodeUID NodeUID 
	 * @param ParentUID ParentUID 
	 */
	constructor(
		public TotalisedEnergyMetric: TotalisedEnergyMetric,
		public NodeName: string,
		public NodeUID: string,
		public ParentUID?: string,
		public Units?: string
	) { }
}

/**
 * IDatapointEnergyMetric
 */
export interface IDatapointEnergyMetric {

	/** DatapointID */
	DatapointID: number;

	/** Current */
	Current: number;

	/** Avg */
	Avg: number;

	/** RunningAvg */
	RunningAvg: number;

	/** Total */
	Total: number;

	/** Diff */
	Diff: number;

	/** Min */
	Min: number;

	/** Max */
	Max: number;

	/** RunTime */
	RunTime?: number;

	/** ParentUID */
	ParentUID?: string;
}

/**
 * DatapointEnergyMetric
 */
export class DatapointEnergyMetric implements IDatapointEnergyMetric {

	/**
	 * Default Constructor
	 * @param DatapointID DatapointID 
	 * @param Current Current 
	 * @param Avg Avg 
	 * @param RunningAvg RunningAvg  
	 * @param Total Total 
	 * @param Diff Diff 
	 * @param Max Max 
	 * @param Min Min 
	 * @param RunTime RunTime 
	 * @param ParentUID ParentUID 
	 */
	constructor(
		public DatapointID: number,
		public Current: number,
		public Avg: number,
		public RunningAvg: number,
		public Total: number,
		public Diff: number,
		public Min: number,
		public Max: number,
		public RunTime: number,
		public ParentUID?: string
	) { }
}

/**
 * ITotalisedEnergyMetric
 */
export interface ITotalisedEnergyMetric {

	/** DatapointID */
	Name: string;

	/** Avg */
	Avg: number;

	/** RunningAvg */
	RunningAvg: number;

	/** Total */
	Total: number;

	/** Diff */
	Diff: number;

	/** Min */
	Min: number;

	/** Max */
	Max: number;

	/** RunTime */
	RunTime?: number;

	/** NodeUID */
	NodeUID?: string;

	/** DataPointIDs */
	DataPointIDs?: number[];

	/** Units */
	Units?: string;
}

/**
 * TotalisedEnergyMetric
 */
export class TotalisedEnergyMetric implements ITotalisedEnergyMetric {

	/**
	 * TotalisedEnergyMetric
	 * @param Name Name 
	 * @param Avg Avg 
	 * @param RunningAvg RunningAvg 
	 * @param Total Total 
	 * @param Diff Diff 
	 * @param Min Min 
	 * @param Max Max 
	 * @param RunTime RunTime 
	 * @param NodeUID NodeUID 
	 */
	constructor(
		public Name: string,
		public Avg: number,
		public RunningAvg: number,
		public Total: number,
		public Diff: number,
		public Min: number,
		public Max: number,
		public RunTime?: number,
		public NodeUID?: string,
		public DataPointIDs?: number[],
		public ParentUID?: string,
		public NodeType?: string,
		public Units?: string
	) { }
}

/** IEnergyReportRequest */
export interface IEnergyReportRequest {

	/** AccountGUID */
	AccountGUID: string;

	/** OrganisationUID */
	OrganisationUID: string;

	/** OrganisationName */
	OrganisationName: string;

	/** ReportTitle */
	ReportTitle: string;

	/** PeriodStartPoint */
	PeriodStartPoint: string;

	/** PeriodEndPoint */
	PeriodEndPoint: string;

	/** PeriodLength */
	PeriodLength: string;

	/** IncludedLocations */
	IncludedLocations: string;

	/** TargetSI */
	TargetSI: string;

	/** SiteCurrent */
	SiteCurrent: number;

	/** SitePeriodAvg */
	SitePeriodAvg: number;

	/** SiteRunningAvg */
	SiteRunningAvg: number;

	/** SiteDifference */
	SiteDifference: number;

	/** SiteTotal */
	SiteTotal: number;

	/** DatapointReportItems */
	DatapointReportItems: DatapointReportItem[];

	/** SiteDeltaChartBase64  */
	SiteDeltaChartBase64: string;

	/** SiteTotalChartBase64 */
	SiteTotalChartBase64: string;

	/** DatapointGraphBase64s */
	DatapointGraphBase64s: { datapointID: any; base64: string }[];

	/** TotalisedReportItems */
	TotalisedReportItems?: TotalisedReportItem[];

	/** TotalisedBase64s */
	TotalisedBase64s?: { nodeUID: string, name: string; base64: string }[];

	/** IncludedNodes */
	IncludedNodes?: string[];
}

/** EnergyReportRequest */
export class EnergyReportRequest implements IEnergyReportRequest {

	/**
	 * Default Constructor
	 * @param AccountGUID AccountGUID 
	 * @param OrganisationUID OrganisationUID 
	 * @param OrganisationName OrganisationName 
	 * @param ReportTitle ReportTitle 
	 * @param PeriodStartPoint PeriodStartPoint 
	 * @param PeriodEndPoint PeriodEndPoint 
	 * @param PeriodLength PeriodLength 
	 * @param IncludedLocations IncludedLocations 
	 * @param TargetSI TargetSI 
	 * @param SiteCurrent SiteCurrent 
	 * @param SitePeriodAvg SitePeriodAvg 
	 * @param SiteRunningAvg SiteRunningAvg 
	 * @param SiteDifference SiteDifference 
	 * @param SiteTotal SiteTotal 
	 * @param DatapointReportItems DatapointReportItems 
	 * @param SiteDeltaChartBase64 SiteDeltaChartBase64 
	 * @param SiteTotalChartBase64 SiteTotalChartBase64 
	 * @param DatapointGraphBase64s DatapointGraphBase64s 
	 * @param TotalisedReportItems TotalisedReportItems 
	 * @param TotalisedBase64s TotalisedCharts 
	 */
	constructor(
		public AccountGUID: string,
		public OrganisationUID: string,
		public OrganisationName: string,
		public ReportTitle: string,
		public PeriodStartPoint: string,
		public PeriodEndPoint: string,
		public PeriodLength: string,
		public IncludedLocations: string,
		public TargetSI: string,
		public SiteCurrent: number,
		public SitePeriodAvg: number,
		public SiteRunningAvg: number,
		public SiteDifference: number,
		public SiteTotal: number,
		public DatapointReportItems: DatapointReportItem[],
		public SiteDeltaChartBase64: string,
		public SiteTotalChartBase64: string,
		public DatapointGraphBase64s: { datapointID: any; base64: string }[],
		public TotalisedReportItems?: TotalisedReportItem[],
		public TotalisedBase64s?: { nodeUID: string, name: string; base64: string }[],
		public IncludedNodes?: string[]
	) { }
}

/**
 * Injectable
 */
@Injectable({
	providedIn: 'root'
})
export class ReportPDFService {

	/** apiUnits */
	private apiReports = "api/{0}/organisation/{1}/reports";

	/** reportServiceURL */
	private reportServiceURL: string = "https://prismpdm-function-reportservice-e2csenawf3gqdebc.uksouth-01.azurewebsites.net/api/";

	/** debugURL */
	private debugURL: string = "http://localhost:7131/api/";

	/** debugging */
	private debugging: boolean = false;

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions Global Functions
	 * @param siteSubscriptions site Subscriptions
	 */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) {
		if (this.debugging) this.reportServiceURL = this.debugURL;
	}

	/**
	 * getReportPDF
	 * @param param1
	 * @param param2
	 * @param param3
	 * @param param4
	 * @param param5
	 * @returns
	 */
	getReportPDF$(param1: string, param2: number, param3: number, param4: string, param5: string): Observable<any> {
		// const url = 'http://localhost:7071/api/reportService/pdf';
		const url = 'https://prismpdm-reportservice.azurewebsites.net/api/reportService/pdf';
		const data = {
			"asset_id": param1,
			"data_from": param2,
			"data_to": param3,
			"acc_id": param4,
			"org_id": param5,
			"events_list": ["test1", "test2"],
			"alarm_list": ["test1", "test"]
		};

		var rtn = this.globalFunctions.fetchAPI(this.http, url, "GET", data, { responseType: 'blob' });
		// console.log(rtn)
		return rtn
	}

	/**
	 * generateEnergyReportPDF
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 */
	generateEnergyReportPDF$(accountGUID: string, organisationUID: string, inputData: EnergyReportRequest) {
		const url = `${this.reportServiceURL}GenerateEnergyReportPDF`;
		// const url = 'http://localhost:7131/api/GenerateEnergyReportPDF';

		let data = JSON.stringify(inputData);

		// let url = this.apiReports;
		// url = url.replace("{0}", accountGUID);
		// url = url.replace("{1}", organisationUID);

		// url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data, { responseType: 'blob' });
	}

	/**
	 * generateEnergyReportPDF
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 */
	generateSimpleEnergyReportPDF$(accountGUID: string, organisationUID: string, inputData: EnergyReportRequest) {
		const url = `${this.reportServiceURL}GenerateSimpleEnergyReportPDF`;
		// const url = 'http://localhost:7131/api/GenerateSimpleEnergyReportPDF';

		let data = JSON.stringify(inputData);

		// let url = this.apiReports;
		// url = url.replace("{0}", accountGUID);
		// url = url.replace("{1}", organisationUID);

		// url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data, { responseType: 'blob' });
	}

	/**
	 * generateEnergyReportExcel
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @param inputData inputData 
	 * @returns 
	 */
	generateEnergyReportExcel$(accountGUID: string, organisationUID: string, inputData: EnergyReportRequest) {
		const url = `${this.reportServiceURL}GenerateEnergyReportExcel`;
		// const url = 'http://localhost:7131/api/GenerateEnergyReportExcel';

		let data = JSON.stringify(inputData);

		// let url = this.apiReports;
		// url = url.replace("{0}", accountGUID);
		// url = url.replace("{1}", organisationUID);

		// url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data, { responseType: 'blob' });
	}

	/**
	 * generateSimpleEnergyReportExcel
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @param inputData inputData 
	 * @returns 
	 */
	generateSimpleEnergyReportExcel$(accountGUID: string, organisationUID: string, inputData: EnergyReportRequest) {
		const url = `${this.reportServiceURL}GenerateSimpleEnergyReportExcel`;
		// const url = 'http://localhost:7131/api/GenerateSimpleEnergyReportExcel';

		let data = JSON.stringify(inputData);

		// let url = this.apiReports;
		// url = url.replace("{0}", accountGUID);
		// url = url.replace("{1}", organisationUID);

		// url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data, { responseType: 'blob' });
	}

	/**
	 * postOrganisationSummaryReport
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @param report report 
	 * @returns 
	 */
	postOrganisationSummaryReport$(accountGUID: string, organisationUID: string, report: SummaryReport) {
		let data = JSON.stringify(report);

		let url = this.apiReports;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationUID);

		url = `${environment.apiUrl}/${url}/summaryReport`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * getOrganisationSummaryReports
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @returns 
	 */
	getOrganisationSummaryReports$(accountGUID: string, organisationUID: string, reportID?: number) {
		let data = {};

		let url = this.apiReports;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationUID);

		url = `${environment.apiUrl}/${url}/summaryReport`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getOrganisationReportHistory
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @returns 
	 */
	getOrganisationReportHistory$(accountGUID: string, organisationUID: string, reportID?: number) {
		let data = {};

		let url = this.apiReports;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationUID);

		url = `${environment.apiUrl}/${url}/reportHistory`;

		if (reportID) url += `?reportID=${reportID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * patchOrganisationSummaryReport
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @param summaryReportID summaryReportID 
	 * @param report report 
	 * @returns 
	 */
	patchOrganisationSummaryReport$(accountGUID: string, organisationUID: string, summaryReportID: number, report: SummaryReport) {
		let data = JSON.stringify(report);

		let url = this.apiReports;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationUID);

		url = `${environment.apiUrl}/${url}/summaryReport/${summaryReportID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * deleteOrganisationSummaryReport
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @param summaryReportID summaryReportID 
	 * @returns 
	 */
	deleteOrganisationSummaryReport$(accountGUID: string, organisationUID: string, summaryReportID: number) {
		let data = {};

		let url = this.apiReports;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationUID);

		url = `${environment.apiUrl}/${url}/summaryReport/${summaryReportID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}
}
